import { useState, useRef, useEffect } from 'react';
import { Search, ChevronDown } from 'lucide-react';

export default function SearchableSelect({ options, value, onChange, placeholder = 'Select…', loading = false, disabled = false, loadingLabel = 'Loading…', noMatchLabel = 'No secrets match.' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = query
    ? options.filter(o => o.toLowerCase().includes(query.toLowerCase()))
    : options;

  function selectOption(name) {
    onChange(name);
    setOpen(false);
    setQuery('');
  }

  function handleKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight(h => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && filtered[highlight]) selectOption(filtered[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  }

  return (
    <div ref={rootRef} className="relative flex-1">
      <div className="relative">
        <Search size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          type="text"
          className={`w-full text-xs py-1.5 pl-7 pr-6 rounded-lg border transition-colors placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-accent-purple/20 focus:border-accent-purple/50 disabled:opacity-50 disabled:cursor-not-allowed ${
            open ? 'bg-white border-accent-purple/50' : 'bg-slate-50 border-slate-200 hover:border-slate-300'
          }`}
          placeholder={loading ? loadingLabel : placeholder}
          value={open ? query : (value || '')}
          onFocus={() => { setOpen(true); setQuery(''); setHighlight(0); }}
          onChange={e => { setQuery(e.target.value); setOpen(true); setHighlight(0); }}
          onKeyDown={handleKeyDown}
          disabled={disabled || loading}
        />
        <ChevronDown size={12} className={`absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none transition-transform ${open ? 'rotate-180' : ''}`} />
      </div>
      {open && !loading && (
        <div className="absolute z-10 mt-1.5 w-full max-h-48 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg py-1">
          {filtered.length === 0 ? (
            <p className="px-3 py-2 text-xs text-slate-400">{noMatchLabel}</p>
          ) : (
            filtered.map((name, i) => (
              <button
                type="button"
                key={name}
                onClick={() => selectOption(name)}
                onMouseEnter={() => setHighlight(i)}
                className={`w-full text-left px-3 py-1.5 text-xs font-mono truncate transition-colors ${
                  i === highlight ? 'bg-slate-100 text-slate-900' : 'text-slate-700 hover:bg-slate-50'
                } ${name === value ? 'font-semibold text-accent-purple' : ''}`}
              >
                {name}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
